import { Link } from "react-router-dom";
import { Phone, CreditCard, Globe, Mail, MessageCircle, Music2 } from "lucide-react";
import { MARKETING_CONFIG } from "@/config/marketing";
import { SOCIAL_PROFILES } from "@/config/social";
import tiktokQrCard from "@/assets/elefa-tiktok-qr-card.png";
import { Logo } from "@/components/ui/Logo";
import { useTranslation } from "react-i18next";

export function ConnectFooter() {
  const { t } = useTranslation("common");

  return (
    <footer className="bg-muted/50 border-t">
      <div className="container py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10 mb-12 text-center max-w-3xl mx-auto">
          {/* Our Services */}
          <div className="flex flex-col items-center">
            <h3 className="font-semibold mb-4 text-foreground">{t("ourServices")}</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <Link to="/#download" className="hover:text-foreground inline-flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {t("web.footer.getAppToCall")}
                </Link>
              </li>
              <li>
                <Link to="/connect-portal/rates" className="hover:text-foreground inline-flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  {t("rates")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div className="flex flex-col items-center">
            <h3 className="font-semibold mb-4 text-foreground">{t("support")}</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <Link to="/connect-portal/help" className="hover:text-foreground inline-flex items-center gap-2">
                  <MessageCircle className="h-4 w-4" />
                  {t("faq")}
                </Link>
              </li>
              <li>
                <a href="mailto:support@elefa.com.au" className="hover:text-foreground inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {t("emailUs")}
                </a>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center">
            <h3 className="font-semibold mb-4 text-foreground">{t("company")}</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <Link to="/connect-portal/about" className="hover:text-foreground">
                  {t("about")}
                </Link>
              </li>
              <li>
                <Link to="/connect-portal/how-it-works" className="hover:text-foreground">
                  {t("howItWorks")}
                </Link>
              </li>
              <li>
                <Link to="/legal/privacy" className="hover:text-foreground">
                  {t("privacyPolicy")}
                </Link>
              </li>
              <li>
                <Link to="/legal/terms" className="hover:text-foreground">
                  {t("termsOfService")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Follow us on TikTok */}
        <div className="border-t pt-10 mb-10 flex flex-col items-center text-center">
          <h3 className="font-semibold mb-4 text-foreground">{t("web.footer.followUs")}</h3>
          <a
            href={SOCIAL_PROFILES.tiktok.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow elefa on TikTok (@elefaConnect)"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Music2 className="h-5 w-5" aria-hidden="true" />
            {SOCIAL_PROFILES.tiktok.handle}
          </a>
          <a
            href={SOCIAL_PROFILES.tiktok.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 block w-40 sm:w-48 rounded-2xl overflow-hidden ring-1 ring-border/60 shadow-sm hover:opacity-90 transition-opacity"
          >
            <img
              src={tiktokQrCard}
              alt="QR code linking to the elefa TikTok profile @elefaConnect"
              width={1200}
              height={1500}
              loading="lazy"
              className="w-full h-auto"
            />
          </a>
        </div>

        <div className="border-t pt-8 text-center">
          <div className="flex items-center justify-center mb-3">
            <Logo variant="header" size="sm" asLink={false} withIcon fullName />
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            {t("web.footer.tagline")}
          </p>


          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-y-3 gap-x-4 text-xs text-muted-foreground">
            <span>© 2026 {MARKETING_CONFIG.company.legalName}. ABN {MARKETING_CONFIG.company.abn}</span>
            <div className="flex gap-4">
              <Link to="/legal/terms" className="hover:text-foreground">{t("terms")}</Link>
              <Link to="/legal/privacy" className="hover:text-foreground">{t("privacy")}</Link>
              <Link to="/connect-portal/help" className="hover:text-foreground">{t("help")}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
